import {makeAutoObservable} from "mobx";
import {ValidatedValue} from "../../helpers/validatedValue";
import Validator from "../../helpers/validator";
import {errorMessages} from "../../helpers/errorMessage";
import {OrderStatusEnum} from "../models/order";
import i18n from "../../i18n/i18n";
import {DangerousGoodInStore, IDangerousGoodInStore} from "./dangerousGoodInStore";
import {CargoType} from "../models/cargo";
import {TicketDetailModel} from "../models/ticket";
import {DateUtils} from "../../helpers/dateUtils";

export interface ITicketStore {
    clearStore: () => void;
    createDangerousGood: () => void;
    removeDangerousGood: (dangerousGoodId: number) => void;

    status: ValidatedValue<OrderStatusEnum>;
    setStatus: (status: OrderStatusEnum) => void;
    cargoType: ValidatedValue<CargoType | "">;
    setCargoType: (cargoType: CargoType | "") => void;
    barcodeNo: ValidatedValue<string>;
    setBarcodeNo: (barcodeNo: string) => void;
    trailerRegNo: ValidatedValue<string>;
    setTrailerRegNo: (trailerRegNo: string) => void;
    transportCompanyId: ValidatedValue<string>;
    setTransportationCompanyId: (transportCompanyId: string) => void;
    lorryRegNo: ValidatedValue<string>;
    setLorryRegNo: (lorryRegNo: string) => void;
    shipownerId: ValidatedValue<string>;
    setShipownerId: (shipownerId: string) => void;
    shipownerName: ValidatedValue<string>;
    setShipownerName: (shipownerName: string) => void;
    voyageNo: ValidatedValue<string>;
    setVoyageNo: (voyageNo: string) => void;
    arrivalDepartureTime: ValidatedValue<Date | null>;
    setArrivalDepartureTime: (arrivalDepartureTime: Date | null) => void;
    ferryId: ValidatedValue<string>;
    setFerryId: (ferryId: string) => void;
    originDestinationPortId: ValidatedValue<string>;
    setOriginDestinationPortId: (originDestinationPortId: string) => void;
    originDestinationCountry: ValidatedValue<string>;
    setOriginDestinationCountry: (originDestinationCountry: string) => void;
    originDestinationCountryName: ValidatedValue<string>;
    setOriginDestinationCountryName: (originDestinationCountryName: string) => void;
    bookingNo: ValidatedValue<string>;
    setBookingNo: (bookingNo: string) => void;
    cargoDesc: ValidatedValue<string>;
    setCargoDesc: (cargoDesc: string) => void;
    weight: ValidatedValue<number | null>;
    setWeight: (weight: number | null) => void;
    length: ValidatedValue<number | null>;
    setLength: (length: number | null) => void;
    width: ValidatedValue<number | null>;
    setWidth: (width: number | null) => void;
    height: ValidatedValue<number | null>;
    setHeight: (height: number | null) => void;
    securingDeclaration: ValidatedValue<boolean>;
    setSecuringDeclaration: (securingDeclaration: boolean) => void;
    dangerousGoods: IDangerousGoodInStore[];
    currentRoute: ValidatedValue<string>;
    setCurrentRoute: (currentRoute: string) => void;
    gateNo: ValidatedValue<string>;
    setGateNo: (gateNo: string) => void;

    ticketIsValid: () => boolean;
    setTicket: (ticketData: TicketDetailModel | null | undefined) => void;
}

export class TicketStore implements ITicketStore {
    _status: ValidatedValue<OrderStatusEnum>;
    _cargoType: ValidatedValue<CargoType | "">;
    _barcodeNo: ValidatedValue<string>;
    _trailerRegNo: ValidatedValue<string>;
    _transportCompanyId: ValidatedValue<string>;
    _lorryRegNo: ValidatedValue<string>;
    _shipownerId: ValidatedValue<string>;
    _shipownerName: ValidatedValue<string>;
    _voyageNo: ValidatedValue<string>;
    _arrivalDepartureTime: ValidatedValue<Date | null>;
    _ferryId: ValidatedValue<string>;
    _originDestinationPortId: ValidatedValue<string>;
    _originDestinationCountry: ValidatedValue<string>;
    _originDestinationCountryName: ValidatedValue<string>;
    _bookingNo: ValidatedValue<string>;
    _cargoDesc: ValidatedValue<string>;
    _weight: ValidatedValue<number | null>;
    _length: ValidatedValue<number | null>;
    _width: ValidatedValue<number | null>;
    _height: ValidatedValue<number | null>;
    _securingDeclaration: ValidatedValue<boolean>;
    _dangerousGoods: IDangerousGoodInStore[] = [];
    _currentRoute: ValidatedValue<string>;
    _gateNo: ValidatedValue<string>;

    constructor() {
        makeAutoObservable(this);

        const ticketRequiredValidators = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const dateWithTimeValidators = {
            name: "correctDateWithTime",
            fn: Validator.isCorrectDateWithTime,
            errorMessage: () => i18n.t("transportation.ticket.errors.correctDateWithTime")
        };

        const onlyNumberValidator = {
            name: "onlyNumber",
            fn: Validator.isOnlyPositiveNumberWithCommaOrEmpty,
            errorMessage: () => i18n.t("errorMessages.onlyNumber")
        };

        this._status = new ValidatedValue(OrderStatusEnum.REGISTERED, [ticketRequiredValidators]);
        this._cargoType = new ValidatedValue("", [ticketRequiredValidators]);
        this._barcodeNo = new ValidatedValue("", [ticketRequiredValidators]);
        this._trailerRegNo = new ValidatedValue("", [ticketRequiredValidators]);
        this._transportCompanyId = new ValidatedValue("");
        this._lorryRegNo = new ValidatedValue("", [ticketRequiredValidators]);
        this._shipownerId = new ValidatedValue("", [ticketRequiredValidators]);
        this._shipownerName = new ValidatedValue("");
        this._voyageNo = new ValidatedValue("", [ticketRequiredValidators]);
        this._arrivalDepartureTime = new ValidatedValue(null, [ticketRequiredValidators, dateWithTimeValidators]);
        this._ferryId = new ValidatedValue("", [ticketRequiredValidators]);
        this._originDestinationPortId = new ValidatedValue("", [ticketRequiredValidators]);
        this._originDestinationCountry = new ValidatedValue("", [ticketRequiredValidators]);
        this._originDestinationCountryName = new ValidatedValue("");
        this._bookingNo = new ValidatedValue("", [ticketRequiredValidators]);
        this._cargoDesc = new ValidatedValue("");
        this._weight = new ValidatedValue(null, [ticketRequiredValidators, onlyNumberValidator]);
        this._length = new ValidatedValue(null, [onlyNumberValidator]);
        this._width = new ValidatedValue(null, [onlyNumberValidator]);
        this._height = new ValidatedValue(null, [onlyNumberValidator]);
        this._securingDeclaration = new ValidatedValue(false);
        this._currentRoute = new ValidatedValue("");

        this._gateNo = new ValidatedValue("");

        this.createDangerousGood();
    }

    createDangerousGood() {
        this._dangerousGoods.push(new DangerousGoodInStore());
    }

    removeDangerousGood(dangerousGoodId: number) {
        this._dangerousGoods.splice(dangerousGoodId, 1);
    }

    clearStore() {
        this._status.clearValidation();
        this._cargoType.clearValidation();
        this._barcodeNo.clearValidation();
        this._trailerRegNo.clearValidation();
        this._transportCompanyId.clearValidation();
        this._lorryRegNo.clearValidation();
        this._shipownerId.clearValidation();
        this._shipownerName.clearValidation();
        this._voyageNo.clearValidation();
        this._arrivalDepartureTime.clearValidation();
        this._ferryId.clearValidation();
        this._originDestinationPortId.clearValidation();
        this._originDestinationCountry.clearValidation();
        this._originDestinationCountryName.clearValidation();
        this._bookingNo.clearValidation();
        this._cargoDesc.clearValidation();
        this._weight.clearValidation();
        this._length.clearValidation();
        this._width.clearValidation();
        this._height.clearValidation();
        this._securingDeclaration.clearValidation();
        this._currentRoute.clearValidation();
        this._gateNo.clearValidation();

        this._status.setValue(OrderStatusEnum.REGISTERED);
        this._cargoType.setValue("");
        this._barcodeNo.setValue("");
        this._trailerRegNo.setValue("");
        this._transportCompanyId.setValue("");
        this._lorryRegNo.setValue("");
        this._shipownerId.setValue("");
        this._shipownerName.setValue("");
        this._voyageNo.setValue("");
        this._arrivalDepartureTime.setValue(null);
        this._ferryId.setValue("");
        this._originDestinationPortId.setValue("");
        this._originDestinationCountry.setValue("");
        this._originDestinationCountryName.setValue("");
        this._bookingNo.setValue("");
        this._cargoDesc.setValue("");
        this._weight.setValue(null);
        this._length.setValue(null);
        this._width.setValue(null);
        this._height.setValue(null);
        this._securingDeclaration.setValue(false);
        this._dangerousGoods = [];
        this._currentRoute.setValue("");

        this._gateNo.setValue("");

        this.createDangerousGood();
    }

    get status() {
        return this._status;
    }

    setStatus(status: OrderStatusEnum) {
        this._status.setValue(status);
    }

    get cargoType() {
        return this._cargoType;
    }

    setCargoType(cargoType: CargoType | "") {
        this._cargoType.setValue(cargoType);
    }

    get barcodeNo() {
        return this._barcodeNo;
    }

    setBarcodeNo(barcodeNo: string) {
        this._barcodeNo.setValue(barcodeNo);
    }

    get trailerRegNo() {
        return this._trailerRegNo;
    }

    setTrailerRegNo(trailerRegNo: string) {
        this._trailerRegNo.setValue(trailerRegNo);
    }

    get transportCompanyId() {
        return this._transportCompanyId;
    }

    setTransportationCompanyId(transportCompanyId: string) {
        this._transportCompanyId.setValue(transportCompanyId);
    }

    get lorryRegNo() {
        return this._lorryRegNo;
    }

    setLorryRegNo(lorryRegNo: string) {
        this._lorryRegNo.setValue(lorryRegNo);
    }

    get shipownerId() {
        return this._shipownerId;
    }

    setShipownerId(shipownerId: string) {
        this._shipownerId.setValue(shipownerId);
    }

    get shipownerName() {
        return this._shipownerName;
    }

    setShipownerName(shipownerName: string) {
        this._shipownerName.setValue(shipownerName);
    }

    get voyageNo() {
        return this._voyageNo;
    }

    setVoyageNo(voyageNo: string) {
        this._voyageNo.setValue(voyageNo);
    }

    get arrivalDepartureTime() {
        return this._arrivalDepartureTime;
    }

    setArrivalDepartureTime(arrivalDepartureTime: Date | null) {
        this._arrivalDepartureTime.setValue(arrivalDepartureTime);
    }

    get ferryId() {
        return this._ferryId;
    }

    setFerryId(ferryId: string) {
        this._ferryId.setValue(ferryId);
    }

    get originDestinationPortId() {
        return this._originDestinationPortId;
    }

    setOriginDestinationPortId(originDestinationPortId: string) {
        this._originDestinationPortId.setValue(originDestinationPortId);
    }

    get originDestinationCountry() {
        return this._originDestinationCountry;
    }

    setOriginDestinationCountry(originDestinationCountry: string) {
        this._originDestinationCountry.setValue(originDestinationCountry);
    }

    get originDestinationCountryName() {
        return this._originDestinationCountryName;
    }

    setOriginDestinationCountryName(originDestinationCountryName: string) {
        this._originDestinationCountryName.setValue(originDestinationCountryName);
    }

    get bookingNo() {
        return this._bookingNo;
    }

    setBookingNo(bookingNo: string) {
        this._bookingNo.setValue(bookingNo);
    }

    get cargoDesc() {
        return this._cargoDesc;
    }

    setCargoDesc(cargoDesc: string) {
        this._cargoDesc.setValue(cargoDesc);
    }

    get weight() {
        return this._weight;
    }

    setWeight(weight: number | null) {
        this._weight.setValue(weight);
    }

    get length() {
        return this._length;
    }

    setLength(length: number | null) {
        this._length.setValue(length);
    }

    get width() {
        return this._width;
    }

    setWidth(width: number | null) {
        this._width.setValue(width);
    }

    get height() {
        return this._height;
    }

    setHeight(height: number | null) {
        this._height.setValue(height);
    }

    get securingDeclaration() {
        return this._securingDeclaration;
    }

    setSecuringDeclaration(securingDeclaration: boolean) {
        this._securingDeclaration.setValue(securingDeclaration);
    }

    get dangerousGoods() {
        return this._dangerousGoods;
    }

    get currentRoute() {
        return this._currentRoute;
    }

    setCurrentRoute(currentRoute: string) {
        this._currentRoute.setValue(currentRoute);
    }

    get gateNo() {
        return this._gateNo;
    }

    setGateNo(gateNo: string) {
        this._gateNo.setValue(gateNo);
    }

    ticketIsValid(): boolean {
        this._status.validate();
        this._cargoType.validate();
        this._barcodeNo.validate();

        this._trailerRegNo.validate();
        this._transportCompanyId.validate();
        this._lorryRegNo.validate();

        this._shipownerId.validate();
        this._voyageNo.validate();
        this._arrivalDepartureTime.validate();
        this._ferryId.validate();
        this._originDestinationPortId.validate();
        this._originDestinationCountry.validate();
        this._bookingNo.validate();

        this._cargoDesc.validate();
        this._weight.validate();
        this._length.validate();
        this._width.validate();
        this._height.validate();
        this._securingDeclaration.validate();

        this._currentRoute.validate();
        this._gateNo.validate();

        let dangerousGoodsAreValid = true;
        const dangerousGoods = this._dangerousGoods;
        dangerousGoods.map((dangerousGood) => {
            if (!this.dangerousGoodsAreValid(dangerousGood)) {
                dangerousGoodsAreValid = false;
            }
        });

        return this._status.isValid && this._cargoType.isValid && this._barcodeNo.isValid &&
            this._trailerRegNo.isValid && this._transportCompanyId.isValid && this._lorryRegNo.isValid &&
            this._shipownerId.isValid && this._voyageNo.isValid &&
            this._arrivalDepartureTime.isValid && this._ferryId.isValid &&
            this._originDestinationPortId.isValid && this._originDestinationCountry.isValid &&
            this._bookingNo.isValid && this._cargoDesc.isValid && this._weight.isValid &&
            this._length.isValid && this._width.isValid && this._height.isValid && this._securingDeclaration.isValid &&
            dangerousGoodsAreValid && this._currentRoute.isValid && this._gateNo.isValid;
    }

    dangerousGoodsAreValid(dangerousGood: IDangerousGoodInStore) {
        dangerousGood.unCodeId.validate();
        dangerousGood.packingId.validate();
        dangerousGood.packingGroup.validate();
        dangerousGood.packingQuantity.validate();
        dangerousGood.weight.validate();

        return dangerousGood.unCodeId.isValid && dangerousGood.packingId.isValid && dangerousGood.packingGroup.isValid &&
            dangerousGood.packingQuantity.isValid && dangerousGood.weight.isValid;
    }

    setTicket(ticketData: TicketDetailModel | null | undefined) {
        if (!ticketData) return;

        this.setStatus(ticketData.status);
        this.setCargoType(ticketData.cargoType);
        this.setCurrentRoute(ticketData.currentRoute);
        this.setBarcodeNo(ticketData.barcodeNo);
        this.setGateNo(ticketData.gateNo || "");
        this.setTrailerRegNo(ticketData.trailerRegNo || "");
        this.setTransportationCompanyId(ticketData.transportCompanyId || "");
        this.setLorryRegNo(ticketData.lorryRegNo || "");
        this.setShipownerId(ticketData.shipownerId || "");
        this.setVoyageNo(ticketData.voyageNo);
        this.setArrivalDepartureTime(DateUtils.getDateAndTimeInPolishTimeZone(ticketData.arrivalDepartureTime || null));
        this.setFerryId(ticketData.ferryId || "");
        this.setOriginDestinationPortId(ticketData.originDestinationPortId);
        this.setOriginDestinationCountry(ticketData.originDestinationCountry);
        this.setBookingNo(ticketData.bookingNo);
        this.setCargoDesc(ticketData.cargoDesc || "");
        this.setWeight(ticketData.weight || null);
        this.setLength(ticketData.length || null);
        this.setHeight(ticketData.height || null);
        this.setWidth(ticketData.width || null);
        this.setSecuringDeclaration(ticketData.securingDeclaration || false);

        const dangerousGoods = ticketData.dangerousGoods;
        const length = dangerousGoods?.length || 0;
        this._dangerousGoods = [];

        if (!length) {
            this.createDangerousGood();
        }

        for (let i = 0; i < length; i++) {
            this.createDangerousGood();
            this.dangerousGoods[i].setPackingId(dangerousGoods![i].packingId || "");
            this.dangerousGoods[i].setPackingGroup(dangerousGoods![i].packingGroup);
            this.dangerousGoods[i].setPackingQuantity(dangerousGoods![i].packingQuantity);
            this.dangerousGoods[i].setWeight(dangerousGoods![i].weight);
            this.dangerousGoods[i].setUnCodeId(dangerousGoods![i].unCodeId || "");
        }
    }
}
